// pages/AnalysisReportPage.jsx
// Full-page visual analysis report - renders a copilot session as a formatted document.
import React, { useState, useEffect, useMemo, useCallback, useRef, useContext, createContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  copilotGetSession,
  copilotExportPptx,
  fetchBrands,
  analysisRunsList,
  citationsAuditDomainUrls,
  copilotSetSectionOverride,
  copilotStage4Prompts,
  copilotPromptInputs,
  copilotRegenerateSection,
  copilotRegenerateBlock,
  analysisRunRevise,
  promptGet,
  promptSaveVersion,
  promptActivate,
  promptRevert,
} from "../services/api";

// ─── Color palette ───────────────────────────────────────────────
const C = {
  navy: "#1a1f36",
  navyLight: "#252b48",
  gold: "#FFCC33",
  goldDark: "#e6b800",
  goldBg: "rgba(255, 204, 51, 0.08)",
  goldBorder: "rgba(255, 204, 51, 0.25)",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
  bg: "#f8f9fc",
  white: "#ffffff",
  text: "#1e293b",
  textMuted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowLg: "0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
};

// ─── Helpers ─────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Slugify a heading title into a stable element id. Tolerant of
 *  non-ASCII chars (Romanian/Spanish/etc.) by normalising and stripping
 *  diacritics before the alnum-only filter. */
function slugifyHeading(title) {
  return (title || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// ─── Inline quote references ─────────────────────────────────────
//
// Insights & Actions tables (and a few other places) cite their
// source as "Q3 ChatGPT, Q7 Gemini, Q12 Perplexity". The string
// alone isn't very helpful - clients want to read the actual
// answer the citation refers to without scrolling back to Layer 3
// or leaving the report page. This pair of helpers wraps every
// matching citation in a small ⓘ icon that:
//   - shows a hover popover with the question + first ~400 chars
//     of the answer (peek)
//   - on click, opens a slide-in drawer with the full answer +
//     a Copy button (deep-dive)
//
// The `answer_index` payload is keyed by Q# → display LLM name
// → { question_text, answer_text }, built upstream by
// build_enhanced_context and persisted on copilot_session.

const QuoteRefContext = createContext(null);

const _LLM_DISPLAY_NAMES = [
  "ChatGPT",
  "Gemini",
  "Perplexity",
  "AI Overview",
  "Claude",
];
// Match e.g. "Q3 ChatGPT" or "Q12 AI Overview". Word-bounded on the
// left, lookahead on the right is non-letter so we don't bleed into
// the next word. Uses an alternation of the known display names so
// random "Q3 banana" doesn't get wrapped.
const _QUOTE_REF_RE = new RegExp(
  `\\bQ(\\d+)\\s+(${_LLM_DISPLAY_NAMES
    .map((n) => n.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("|")})(?![A-Za-z])`,
  "g",
);

function QuoteRef({ qNum, llm }) {
  const ctx = useContext(QuoteRefContext);
  const [hovering, setHovering] = useState(false);
  const entry = ctx?.answerIndex?.[`Q${qNum}`]?.[llm] || null;
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      ctx?.openAnswer?.(qNum, llm);
    },
    [ctx, qNum, llm],
  );
  // No data for this citation? Render the bare text - the icon would
  // be misleading.
  if (!entry) {
    return <span>{`Q${qNum} ${llm}`}</span>;
  }
  const preview = (entry.answer_text || "").trim().slice(0, 360);
  const truncated = (entry.answer_text || "").length > 360;
  return (
    <span
      style={{ position: "relative", whiteSpace: "nowrap", display: "inline-flex", alignItems: "baseline", gap: 2 }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <span>{`Q${qNum} ${llm}`}</span>
      <button
        type="button"
        aria-label={`Show answer Q${qNum} ${llm}`}
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          marginLeft: 3,
          padding: 0,
          border: "none",
          borderRadius: "50%",
          background: "rgba(37,99,235,0.10)",
          color: "#1d4ed8",
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "Georgia, serif",
          fontStyle: "italic",
          cursor: "pointer",
          lineHeight: 1,
          verticalAlign: "super",
          transition: "background 0.15s",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#bfdbfe";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "rgba(37,99,235,0.10)";
        }}
      >
        i
      </button>
      {hovering && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            left: 0,
            top: "100%",
            marginTop: 6,
            zIndex: 50,
            width: 380,
            maxWidth: "min(380px, 90vw)",
            padding: "12px 14px",
            background: "#0f172a",
            color: "#f1f5f9",
            borderRadius: 8,
            boxShadow: "0 10px 24px rgba(15,23,42,0.28)",
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: "normal",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#fbbf24",
              marginBottom: 4,
            }}
          >
            {`Q${qNum} • ${llm}`}
          </div>
          {entry.question_text ? (
            <div style={{ fontStyle: "italic", color: "#cbd5e1", marginBottom: 6 }}>
              {entry.question_text.slice(0, 200)}
              {entry.question_text.length > 200 ? "…" : ""}
            </div>
          ) : null}
          <div>
            {preview}
            {truncated ? "…" : ""}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: "#94a3b8",
              marginTop: 8,
              fontStyle: "italic",
            }}
          >
            Click the ⓘ for the full answer + copy
          </div>
        </span>
      )}
    </span>
  );
}

/** A domain rendered in the Link Analysis tables, carrying a ⓘ that opens the
 *  right-pane drawer with every citation from that domain in this audit. Mirrors
 *  QuoteRef: the formatters emit the domain as a `[domain](#domain:domain)` marker
 *  link, and ReportLink swaps that for this component. */
function DomainRef({ domain }) {
  const ctx = useContext(QuoteRefContext);
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      ctx?.openDomain?.(domain);
    },
    [ctx, domain],
  );
  return (
    <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "baseline", gap: 2 }}>
      <span>{domain}</span>
      <button
        type="button"
        aria-label={`Show citations for ${domain}`}
        onClick={handleClick}
        title={`Citations from ${domain}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          marginLeft: 3,
          padding: 0,
          border: "none",
          borderRadius: "50%",
          background: "rgba(37,99,235,0.10)",
          color: "#1d4ed8",
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "Georgia, serif",
          fontStyle: "italic",
          cursor: "pointer",
          lineHeight: 1,
          verticalAlign: "super",
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = "#bfdbfe"; }}
        onMouseOut={(e) => { e.currentTarget.style.background = "rgba(37,99,235,0.10)"; }}
      >
        i
      </button>
    </span>
  );
}

/** A truncated quote in a report table, carrying a ⓘ that pops the FULL quote.
 *  Formatters emit `[short…](#quote:<url-encoded full>)`; ReportLink swaps that
 *  marker for this. Lightweight click-to-toggle popover. */
function FullQuoteRef({ full, children }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }, []);
  return (
    <span style={{ position: "relative", display: "inline" }}>
      <span>{children}</span>
      <button
        type="button"
        aria-label="Show full quote"
        title="Show full quote"
        onClick={toggle}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 14, height: 14, marginLeft: 3, padding: 0, border: "none",
          borderRadius: "50%", background: "rgba(37,99,235,0.10)", color: "#1d4ed8",
          fontSize: 10, fontWeight: 700, fontFamily: "Georgia, serif", fontStyle: "italic",
          cursor: "pointer", lineHeight: 1, verticalAlign: "super",
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = "#bfdbfe"; }}
        onMouseOut={(e) => { e.currentTarget.style.background = "rgba(37,99,235,0.10)"; }}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", zIndex: 60, left: 0, top: "100%", marginTop: 4,
            minWidth: 240, maxWidth: 460, whiteSpace: "normal", fontStyle: "normal",
            fontWeight: 400, background: "#fff", color: "#1e293b",
            border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px",
            boxShadow: "0 12px 34px rgba(15,23,42,0.16)", fontSize: 12.5, lineHeight: 1.5,
          }}
        >
          <span style={{ display: "block", marginBottom: 6, fontSize: 10.5, fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMuted }}>
            Full quote
          </span>
          {full}
          <button type="button" onClick={toggle} aria-label="Close"
            style={{ position: "absolute", top: 4, right: 6, border: "none", background: "transparent",
              cursor: "pointer", fontSize: 14, color: C.textMuted, lineHeight: 1 }}>×</button>
        </span>
      )}
    </span>
  );
}

/** Walk a React children tree, replacing every "Q\d+ <LLM>" text
 *  match with a QuoteRef component. Non-string children pass through.
 *  Returns new children - same structure minus the text-match swaps. */
function renderWithQuoteRefs(children) {
  if (children == null) return children;
  if (typeof children === "string") {
    // Reset stateful lastIndex on every entry - `g`-flagged regexes
    // are stateful and a previous call could otherwise mis-position
    // the scan and miss matches at the start of `children`.
    _QUOTE_REF_RE.lastIndex = 0;
    if (!_QUOTE_REF_RE.test(children)) return children;
    _QUOTE_REF_RE.lastIndex = 0;
    const out = [];
    let lastIdx = 0;
    let m;
    let key = 0;
    while ((m = _QUOTE_REF_RE.exec(children)) !== null) {
      const before = children.slice(lastIdx, m.index);
      if (before) out.push(before);
      out.push(<QuoteRef key={`qr-${key++}`} qNum={m[1]} llm={m[2]} />);
      lastIdx = m.index + m[0].length;
    }
    const tail = children.slice(lastIdx);
    if (tail) out.push(tail);
    return out;
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => {
      const r = renderWithQuoteRefs(c);
      // Preserve element keys when wrapping arrays so React doesn't
      // complain about missing keys on the inner fragments.
      if (Array.isArray(r)) {
        return <React.Fragment key={`grp-${i}`}>{r}</React.Fragment>;
      }
      return r;
    });
  }
  // React element - recurse into its children.
  if (React.isValidElement(children)) {
    const inner = children.props?.children;
    if (inner == null) return children;
    return React.cloneElement(children, undefined, renderWithQuoteRefs(inner));
  }
  return children;
}

/** Split markdown into cards. Splits on ### (or ## if any are present -
 *  rare since the new structure rarely uses them). #### subsection
 *  headings stay inside their parent card so the per-card markdown
 *  remains a single rendered block. */
function splitIntoCards(markdown) {
  const lines = markdown.split("\n");
  const cards = [];
  let current = null;
  const seen = new Set();
  const _id = (title) => {
    let id = slugifyHeading(title);
    let candidate = id;
    let n = 2;
    while (seen.has(candidate)) {
      candidate = `${id}-${n}`;
      n += 1;
    }
    seen.add(candidate);
    return candidate;
  };

  for (const line of lines) {
    // Match # (audit hero) or ## (section card). ###/####/##### stay
    // inside the parent card so table-level headings (### Brand Universe
    // - Reach) and sub-headings (#### / #####) ride along with their
    // surrounding content instead of being orphaned in empty cards.
    const m = line.match(/^(#{1,2})\s+(.+)/);
    if (m) {
      if (current) cards.push(current);
      const title = m[2].trim();
      current = { title, id: _id(title), lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      // Content before the first heading - render as preamble.
      if (!cards.length && line.trim()) {
        if (!current) {
          current = { title: null, id: "preamble", lines: [] };
        }
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
      }
    }
  }
  if (current) cards.push(current);
  return cards;
}

// ─── Markdown renderers for report styling ───────────────────────

// Click-to-sort helpers ──────────────────────────────────────────
// Tables in the report carry numeric and string columns; users want
// to sort by clicking column headers without any filter UI. The
// helpers below extract a sort key per cell, classify cells as
// numeric vs string, and pin "Total" / hidden-rows summary lines at
// the bottom regardless of direction so the bottom-line aggregates
// don't vanish into the middle of a sorted list.

const _NUMERIC_STRIP_RE = /[%,×x+\s]|pp|→/gi;

function _flattenCellText(cell) {
  if (!cell) return "";
  const text = flattenChildrenToText(cell.props ? cell.props.children : cell);
  return (text || "").trim();
}

function _isPinnedFirstCell(text) {
  // Detect "Total" / "(N rows hidden …)" / placeholder labels so the
  // sort doesn't drag them out of the bottom of the table.
  const t = (text || "").trim().replace(/^\*+|\*+$/g, "").toLowerCase();
  if (!t) return false;
  if (t.startsWith("total")) return true;
  if (t.startsWith("(") && t.includes("rows hidden")) return true;
  return false;
}

function _parseSortValue(text) {
  // Returns { num, isNum, str } - `isNum` true when the cell is a
  // numeric-looking token (after stripping units/commas/pp/etc.). The
  // raw string (lowercased, trimmed) is the fallback sort key.
  const t = (text || "").trim().replace(/^\*+|\*+$/g, "");
  if (t === "" || t === "—" || t === "-" || t === "n/a") {
    // Treat empty / dash / n/a as "least" (sort to end on asc, top on
    // desc) - meaning numerically -Infinity for asc, +Infinity for desc.
    // Caller decides via direction. Return NaN to let the comparator
    // bucket these.
    return { num: NaN, isNum: true, str: "" };
  }
  const stripped = t.replace(_NUMERIC_STRIP_RE, "");
  const n = parseFloat(stripped);
  if (!Number.isNaN(n) && /^-?\d/.test(stripped)) {
    return { num: n, isNum: true, str: t.toLowerCase() };
  }
  return { num: NaN, isNum: false, str: t.toLowerCase() };
}

function _sortRows(rows, colIdx, direction) {
  if (!rows.length || colIdx == null) return rows;
  // Split into pinned (Total / hidden-rows summary) vs sortable.
  const sortable = [];
  const pinnedBottom = [];
  rows.forEach((row) => {
    const cells = React.Children.toArray(row.props?.children || []);
    const firstText = _flattenCellText(cells[0]);
    if (_isPinnedFirstCell(firstText)) {
      pinnedBottom.push(row);
    } else {
      sortable.push(row);
    }
  });
  // Detect column type by sampling sortable rows.
  let columnIsNumeric = true;
  for (const row of sortable.slice(0, 5)) {
    const cells = React.Children.toArray(row.props?.children || []);
    const v = _parseSortValue(_flattenCellText(cells[colIdx]));
    if (v.str !== "" && !v.isNum) {
      columnIsNumeric = false;
      break;
    }
  }
  const sign = direction === "desc" ? -1 : 1;
  sortable.sort((a, b) => {
    const aCells = React.Children.toArray(a.props?.children || []);
    const bCells = React.Children.toArray(b.props?.children || []);
    const av = _parseSortValue(_flattenCellText(aCells[colIdx]));
    const bv = _parseSortValue(_flattenCellText(bCells[colIdx]));
    if (columnIsNumeric) {
      // Push NaN cells (- / n/a) to the end of the asc sort so the
      // top is always meaningful values.
      const aNan = Number.isNaN(av.num);
      const bNan = Number.isNaN(bv.num);
      if (aNan && bNan) return 0;
      if (aNan) return 1;
      if (bNan) return -1;
      return sign * (av.num - bv.num);
    }
    if (av.str < bv.str) return -1 * sign;
    if (av.str > bv.str) return 1 * sign;
    return 0;
  });
  return [...sortable, ...pinnedBottom];
}

function ReportTable({ children, ...props }) {
  const [sort, setSort] = useState(null); // { colIdx: number, direction: 'asc' | 'desc' } | null

  // Extract thead + tbody from the markdown-rendered children. The
  // top-level `children` is typically [thead, tbody] (each a React
  // element). Markdown tables sometimes interleave with whitespace
  // strings - we filter those out.
  const arr = React.Children.toArray(children);
  let theadEl = null;
  let tbodyEl = null;
  for (const c of arr) {
    if (!React.isValidElement(c)) continue;
    const isThead =
      c.type === ReportThead ||
      (typeof c.type === "string" && c.type === "thead");
    const isTbody = typeof c.type === "string" && c.type === "tbody";
    if (isThead) theadEl = c;
    else if (isTbody) tbodyEl = c;
  }

  // Body rows + sort.
  const bodyRows = tbodyEl
    ? React.Children.toArray(tbodyEl.props.children).filter((r) => React.isValidElement(r))
    : [];
  const sortedRows =
    sort && bodyRows.length > 1 ? _sortRows(bodyRows, sort.colIdx, sort.direction) : bodyRows;

  // Header cells - wrap with click + sort indicator.
  let headerRow = null;
  if (theadEl) {
    const theadKids = React.Children.toArray(theadEl.props.children).filter((r) =>
      React.isValidElement(r),
    );
    headerRow = theadKids[0] || null;
  }
  const headerCells = headerRow
    ? React.Children.toArray(headerRow.props.children).filter((c) => React.isValidElement(c))
    : [];

  const onHeaderClick = (idx) => {
    setSort((cur) => {
      if (!cur || cur.colIdx !== idx) return { colIdx: idx, direction: "asc" };
      if (cur.direction === "asc") return { colIdx: idx, direction: "desc" };
      return null; // third click clears the sort
    });
  };

  return (
    <div style={{ overflowX: "auto", margin: "16px 0", borderRadius: 8, border: `1px solid ${C.border}` }}>
      <table
        {...props}
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        {theadEl && headerRow && (
          <ReportThead>
            <tr>
              {headerCells.map((cell, idx) => {
                const isActive = sort && sort.colIdx === idx;
                const arrow = isActive ? (sort.direction === "asc" ? "▲" : "▼") : "";
                return (
                  <th
                    key={idx}
                    onClick={() => onHeaderClick(idx)}
                    title="Click to sort"
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: "0.02em",
                      // Explicit color + background instead of inheriting
                      // from <thead> - defensive against the active-state
                      // gold tint blending with inherited dark text and
                      // washing out the label.
                      color: "#ffffff",
                      background: isActive ? "rgba(255,204,51,0.22)" : C.navy,
                      borderBottom: isActive
                        ? `2px solid ${C.gold}`
                        : `2px solid ${C.gold}`,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = C.navy;
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "inherit",
                      }}
                    >
                      {cell.props ? cell.props.children : cell}
                      <span
                        aria-hidden="true"
                        style={{
                          fontSize: 11,
                          opacity: 1,
                          color: isActive ? C.gold : "rgba(255,255,255,0.85)",
                          width: 10,
                          display: "inline-block",
                          fontWeight: 700,
                        }}
                      >
                        {arrow || "↕"}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </ReportThead>
        )}
        {tbodyEl && <tbody>{sortedRows}</tbody>}
      </table>
    </div>
  );
}

function ReportThead({ children, ...props }) {
  return (
    <thead
      {...props}
      style={{
        background: C.navy,
        color: C.white,
      }}
    >
      {children}
    </thead>
  );
}

function ReportTh({ children, ...props }) {
  return (
    <th
      {...props}
      style={{
        padding: "10px 14px",
        textAlign: "left",
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: "0.02em",
        borderBottom: `2px solid ${C.gold}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function ReportTr({ children, ...props }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "rgba(37, 99, 235, 0.04)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      {children}
    </tr>
  );
}

function ReportTd({ children, style: _, isHeader, ...props }) {
  return (
    <td
      {...props}
      style={{
        padding: "10px 14px",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 14,
        color: C.text,
        verticalAlign: "top",
      }}
    >
      {renderWithQuoteRefs(children)}
    </td>
  );
}

function ReportBlockquote({ children, ...props }) {
  return (
    <blockquote
      {...props}
      style={{
        margin: "16px 0",
        padding: "14px 20px",
        borderLeft: `4px solid ${C.gold}`,
        background: C.goldBg,
        borderRadius: "0 8px 8px 0",
        color: C.text,
        fontStyle: "italic",
        fontSize: 15,
        lineHeight: 1.7,
      }}
    >
      {children}
    </blockquote>
  );
}

function ReportCode({ inline, className, children, ...props }) {
  const val = String(children || "");
  if (inline) {
    return (
      <code
        style={{
          background: "#f1f5f9",
          padding: "2px 6px",
          borderRadius: 4,
          fontSize: "0.9em",
          color: "#0f172a",
          fontFamily: "'SF Mono', 'Fira Code', monospace",
        }}
        {...props}
      >
        {val}
      </code>
    );
  }
  return (
    <pre
      style={{
        background: "#0f172a",
        color: "#e2e8f0",
        padding: 16,
        borderRadius: 8,
        overflow: "auto",
        fontSize: 13,
        lineHeight: 1.6,
        margin: "12px 0",
      }}
    >
      <code className={className || ""} {...props}>
        {val}
      </code>
    </pre>
  );
}

function ReportLink({ children, ...props }) {
  // Domain marker links emitted by the Link Analysis formatters
  // (`[domain](#domain:domain)`) become a ⓘ that opens the citation drawer
  // instead of a normal external link.
  const href = props.href || "";
  if (href.startsWith("#domain:")) {
    return <DomainRef domain={decodeURIComponent(href.slice("#domain:".length))} />;
  }
  if (href.startsWith("#quote:")) {
    return <FullQuoteRef full={decodeURIComponent(href.slice("#quote:".length))}>{children}</FullQuoteRef>;
  }
  return (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      style={{ color: C.blue, textDecoration: "underline", textUnderlineOffset: 2 }}
    >
      {children}
    </a>
  );
}

/** Flatten ReactMarkdown children (which can be a mix of strings + nodes)
 *  to a plain string so a heading's text is available for its slug `id`.
 *  (The TOC then re-stamps a stable `toc-N` id via buildTocFromDom, but the
 *  slug fallback keeps headings addressable before that pass runs.) */
function flattenChildrenToText(children) {
  if (children == null) return "";
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(flattenChildrenToText).join("");
  if (typeof children === "object" && children.props) {
    return flattenChildrenToText(children.props.children);
  }
  return "";
}

function ReportH1({ children, ...props }) {
  // Defensive: `#` headings should be consumed as card titles by
  // splitIntoCards, but if any leak into the card body render them
  // with the same prominence as a card title.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h1
      {...props}
      id={id}
      style={{
        fontSize: 24,
        fontWeight: 800,
        color: C.navy,
        margin: "28px 0 14px",
        paddingBottom: 12,
        borderBottom: `3px solid ${C.gold}`,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h1>
  );
}

function ReportH2({ children, ...props }) {
  // Defensive: `##` headings should be consumed as card titles. If they
  // leak through, mirror the SectionCard heading style.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h2
      {...props}
      id={id}
      style={{
        fontSize: 22,
        fontWeight: 800,
        color: C.navy,
        margin: "26px 0 14px",
        paddingBottom: 12,
        borderBottom: `3px solid ${C.gold}`,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h2>
  );
}

function ReportH3({ children, ...props }) {
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h3
      {...props}
      id={id}
      style={{
        fontSize: 18,
        fontWeight: 700,
        color: C.navy,
        margin: "24px 0 12px",
        lineHeight: 1.4,
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h3>
  );
}

function ReportH4({ children, ...props }) {
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h4
      {...props}
      id={id}
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: C.navyLight,
        margin: "20px 0 8px",
        lineHeight: 1.4,
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h4>
  );
}

function ReportH5({ children, ...props }) {
  // Sub-sub-section headings ("##### Brand × Competitor - Sample sentences"
  // etc.). Slightly smaller than H4 and uppercase-tracked so the visual
  // hierarchy stays clear without competing with the parent H4.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h5
      {...props}
      id={id}
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: C.navyLight,
        margin: "18px 0 6px",
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h5>
  );
}

function ReportStrong({ children }) {
  // Detect "insight"-style bold lines (heuristic: bold text that forms its own block)
  return <strong style={{ color: C.navy, fontWeight: 700 }}>{children}</strong>;
}

function ReportParagraph({ children, ...props }) {
  // Check if the paragraph content looks like an insight callout
  const text = String(
    Array.isArray(children) ? children.map((c) => (typeof c === "string" ? c : "")).join("") : children || ""
  );
  const isInsight =
    /^(insight|key finding|takeaway|recommendation|note|important)/i.test(text.trim());

  if (isInsight) {
    return (
      <div
        style={{
          margin: "14px 0",
          padding: "14px 18px",
          background: C.blueBg,
          border: `1px solid ${C.blueBorder}`,
          borderRadius: 8,
          fontSize: 15,
          lineHeight: 1.7,
          color: C.text,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: C.blue,
            marginRight: 10,
            verticalAlign: "middle",
            flexShrink: 0,
          }}
        />
        {renderWithQuoteRefs(children)}
      </div>
    );
  }

  return (
    <p {...props} style={{ margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {renderWithQuoteRefs(children)}
    </p>
  );
}

const markdownComponents = {
  table: ReportTable,
  thead: ReportThead,
  th: ReportTh,
  tr: ReportTr,
  td: ReportTd,
  blockquote: ReportBlockquote,
  code: ReportCode,
  a: ReportLink,
  h1: ReportH1,
  h2: ReportH2,
  h3: ReportH3,
  h4: ReportH4,
  h5: ReportH5,
  strong: ReportStrong,
  p: ReportParagraph,
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 24, margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 24, margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {children}
    </ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: `2px solid ${C.border}`,
        margin: "28px 0",
      }}
    />
  ),
};

// ─── Answer drawer ───────────────────────────────────────────────
//
// Slide-in panel from the right showing the full answer text behind a
// (Q#, LLM) citation. Triggered when the user clicks the ⓘ icon next to
// any inline citation. Owns no state of its own - controlled by
// AnalysisReportPage via the open/close props so the same drawer
// instance handles citations from anywhere in the report.

// ─── Domain citation drawer ──────────────────────────────────────
// Right-pane slide-in listing every citation (URL) from a given domain
// in this audit. Opened by clicking the ⓘ next to a domain in the Link
// Analysis tables (DomainRef → openDomain(domain) → here). Mirrors the
// AnswerDrawer shell; fetches on open via citationsDomainUrls.
function DomainDrawer({ open, domain, brandId, auditId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [urls, setUrls] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !domain) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrls([]);
    if (!auditId) {
      setError("This report has no linked audit, so per-domain citations are unavailable.");
      setLoading(false);
      return undefined;
    }
    // Scan the audit matrix (same source as the report's domain counts) - the
    // `citations` table is empty for analysis-run audits.
    citationsAuditDomainUrls(auditId, domain)
      .then((data) => {
        if (!cancelled) setUrls((data && data.urls) || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load citations.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, domain, auditId]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.32)", zIndex: 200 }} />
      <aside
        role="dialog"
        aria-label={`Citations from ${domain}`}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: "min(560px, 96vw)",
          background: C.white, boxShadow: "-12px 0 40px rgba(15,23,42,0.18)", zIndex: 201,
          display: "flex", flexDirection: "column",
        }}
      >
        <header style={{
          padding: "20px 24px 14px", borderBottom: `1px solid ${C.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", color: C.blue }}>
              Citations from domain
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginTop: 2 }}>{domain}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close citation drawer"
            style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1,
              cursor: "pointer", color: C.textMuted }}>×</button>
        </header>
        <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>
          {loading && <p style={{ color: C.textMuted, fontSize: 14 }}>Loading citations…</p>}
          {error && <p style={{ color: "#b91c1c", fontSize: 14 }}>{error}</p>}
          {!loading && !error && urls.length === 0 && (
            <p style={{ color: C.textMuted, fontSize: 14 }}>No citations recorded for this domain in this audit.</p>
          )}
          {!loading && !error && urls.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {urls.map((u, i) => (
                <li key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                  <a href={u.full_url} target="_blank" rel="noreferrer"
                    style={{ color: C.blue, textDecoration: "underline", wordBreak: "break-all", fontSize: 13.5 }}>
                    {u.full_url}
                  </a>
                  <div style={{ color: C.textMuted, fontSize: 12, marginTop: 3 }}>
                    {u.appearances != null ? `${u.appearances} appearance${u.appearances === 1 ? "" : "s"}` : ""}
                    {u.model ? ` · ${u.model}` : ""}
                  </div>
                  {u.quote ? (
                    <div style={{ color: C.text, fontSize: 12.5, marginTop: 5, fontStyle: "italic",
                      borderLeft: `3px solid ${C.border}`, paddingLeft: 8 }}>
                      {u.quote}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

function AnswerDrawer({ open, qNum, llm, entry, onClose }) {
  const [copied, setCopied] = useState(false);

  // Reset the copied-toast state every time the drawer opens for a
  // different citation, otherwise the badge would flicker as a stale
  // "Copied!" indicator on a fresh entry.
  useEffect(() => {
    setCopied(false);
  }, [qNum, llm, open]);

  // Close on Escape so power users don't have to mouse to the X.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleCopy = useCallback(async () => {
    const text = entry?.answer_text || "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can fail in some browsers / contexts. Fall back
      // to a textarea-based copy so users always have a path.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      } catch {
        /* give up silently */
      }
      document.body.removeChild(ta);
    }
  }, [entry]);

  if (!open) return null;

  return (
    <>
      {/* backdrop - clicking outside closes */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.32)",
          zIndex: 200,
        }}
      />
      <aside
        role="dialog"
        aria-label={`Answer for Q${qNum} ${llm}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 96vw)",
          background: C.white,
          boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
          zIndex: 201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px 14px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              {`Q${qNum} • ${llm}`}
            </div>
            {entry?.question_text ? (
              <h2
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: C.navy,
                  margin: 0,
                  lineHeight: 1.4,
                }}
              >
                {entry.question_text}
              </h2>
            ) : (
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: C.textMuted,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                (question text unavailable)
              </h2>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close answer drawer"
            style={{
              flexShrink: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              fontSize: 22,
              lineHeight: 1,
              color: C.textMuted,
              borderRadius: 6,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            ×
          </button>
        </header>
        <div
          style={{
            padding: "16px 24px 14px",
            display: "flex",
            gap: 10,
            alignItems: "center",
            borderBottom: `1px solid ${C.border}`,
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={handleCopy}
            style={{
              padding: "7px 14px",
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              background: copied ? "#dcfce7" : C.white,
              color: copied ? "#166534" : C.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {copied ? "✓ Copied" : "Copy answer"}
          </button>
          <span style={{ fontSize: 12, color: C.textMuted }}>
            Esc to close
          </span>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px 28px",
            fontSize: 14.5,
            lineHeight: 1.7,
            color: C.text,
          }}
        >
          {entry?.answer_text ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={answerDrawerMarkdownComponents}
            >
              {entry.answer_text}
            </ReactMarkdown>
          ) : (
            <span style={{ color: C.textMuted, fontStyle: "italic" }}>
              (answer text unavailable)
            </span>
          )}
        </div>
      </aside>
    </>
  );
}

// Slim subset of the report's markdown components, scoped to the
// AnswerDrawer's body. Different from the main report renderer in
// two ways: (a) we don't run renderWithQuoteRefs over the children
// - there are no nested citations to process inside an LLM answer
// - and (b) headings are rendered at smaller sizes since the drawer
// body is narrower than a section card. Also drops the "insight
// callout" auto-detection, which would mis-fire on prose.
const answerDrawerMarkdownComponents = {
  table: ReportTable,
  thead: ReportThead,
  th: ReportTh,
  tr: ReportTr,
  td: ({ children, ...props }) => (
    <td
      {...props}
      style={{
        padding: "8px 12px",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 13,
        color: C.text,
        verticalAlign: "top",
      }}
    >
      {children}
    </td>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      {...props}
      style={{
        margin: "12px 0",
        padding: "10px 14px",
        borderLeft: `3px solid ${C.gold}`,
        background: C.goldBg,
        borderRadius: "0 6px 6px 0",
        color: C.text,
        fontStyle: "italic",
        fontSize: 13.5,
        lineHeight: 1.6,
      }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code
          {...props}
          style={{
            background: "#f1f5f9",
            padding: "1px 5px",
            borderRadius: 4,
            fontSize: "0.92em",
            color: "#0f172a",
            fontFamily: "'SF Mono', 'Fira Code', monospace",
          }}
        >
          {children}
        </code>
      );
    }
    return (
      <pre
        style={{
          background: "#0f172a",
          color: "#e2e8f0",
          padding: "12px 14px",
          borderRadius: 8,
          overflow: "auto",
          fontSize: 12.5,
          lineHeight: 1.55,
          margin: "10px 0",
        }}
      >
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    );
  },
  a: ({ href, children, ...props }) => (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: C.blue, textDecoration: "underline" }}
    >
      {children}
    </a>
  ),
  h1: ({ children }) => (
    <h1 style={{ fontSize: 18, fontWeight: 800, color: C.navy, margin: "16px 0 8px", lineHeight: 1.3 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: "14px 0 6px", lineHeight: 1.3 }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: "12px 0 6px", lineHeight: 1.35 }}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navyLight, margin: "10px 0 4px", lineHeight: 1.4 }}>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 style={{ fontSize: 13, fontWeight: 700, color: C.navyLight, margin: "8px 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{children}</h5>
  ),
  strong: ({ children }) => <strong style={{ color: C.navy, fontWeight: 700 }}>{children}</strong>,
  p: ({ children, ...props }) => (
    <p {...props} style={{ margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 22, margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 22, margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>{children}</ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: 3 }}>{children}</li>,
  hr: () => <hr style={{ border: "none", borderTop: `1px solid ${C.border}`, margin: "16px 0" }} />,
};

// ─── Admin metadata drawer ───────────────────────────────────────
//
// Slide-in panel from the right showing report metadata that doesn't
// belong in the main reading flow: cost, methodology version, audit
// trail (brand spec / models / question count snapshot), and the
// changelog. Triggered by the ⚙ icon in the header.
//
// Receives the admin-only cards filtered out of `splitIntoCards` so
// the same Audit Trail / Methodology Version content shipped in the
// MD/PPTX export is preserved here for traceability - just hidden
// from the client-facing main flow.

function AdminMetaDrawer({
  open,
  onClose,
  session,
  brandName,
  modelName,
  sessionDate,
  adminCards,
  auditQuestionsCount,
  onShowQuestions,
  onShowContext,
}) {
  // Close on Escape - same pattern as AnswerDrawer.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const audit = session?.audit;
  const cost = session?.session_cost_usd;
  const ctxKChars = session?.context_blob_chars
    ? `${(session.context_blob_chars / 1000).toFixed(0)}K chars`
    : "-";

  const _row = (label, value) => (
    <div
      key={label}
      style={{
        display: "flex",
        gap: 12,
        padding: "10px 0",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ flex: "0 0 140px", color: C.textMuted, fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, color: C.text, wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.32)",
          zIndex: 200,
        }}
      />
      <aside
        role="dialog"
        aria-label="Report configuration & metadata"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(620px, 96vw)",
          background: C.white,
          boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
          zIndex: 201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px 14px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              Configuration
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: C.navy,
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              Report metadata &amp; audit trail
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close metadata drawer"
            style={{
              flexShrink: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              fontSize: 22,
              lineHeight: 1,
              color: C.textMuted,
              borderRadius: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            ×
          </button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 28px" }}>
          {/* Top-line snapshot */}
          <section style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 6,
              }}
            >
              Snapshot
            </div>
            {_row("Cost", cost != null ? `$${Number(cost).toFixed(4)}` : "-")}
            {_row("Brand", brandName)}
            {_row("Generated", sessionDate)}
            {_row("Copilot model", modelName)}
            {audit && _row("Audit", audit.title || `#${audit.id}`)}
            {audit?.created_at && _row("Audit run at", formatDate(audit.created_at))}
            {audit?.models?.length > 0 && _row("Models queried", audit.models.join(", "))}
            {_row("Context blob size", ctxKChars)}
          </section>

          {/* Inspect - debug / audit-tooling deep-dives. Open the
              existing modal pickers (questions list, layered context
              blob) which are rendered at the page root. */}
          {(onShowQuestions || onShowContext) && (
            <section style={{ marginTop: 24 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.textMuted,
                  marginBottom: 8,
                }}
              >
                Inspect
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {onShowQuestions && (
                  <button
                    type="button"
                    onClick={onShowQuestions}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.white,
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                  >
                    <span>
                      View questions
                      {typeof auditQuestionsCount === "number" && auditQuestionsCount > 0
                        ? ` (${auditQuestionsCount})`
                        : ""}
                    </span>
                    <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 400 }}>
                      Q1 - Q{auditQuestionsCount || "n"} text
                    </span>
                  </button>
                )}
                {onShowContext && (
                  <button
                    type="button"
                    onClick={onShowContext}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.white,
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                  >
                    <span>View context blob</span>
                    <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 400 }}>
                      Layered LLM input ({ctxKChars})
                    </span>
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Render the Audit Trail / Methodology Version / Changelog
              cards filtered out of the main column. Same markdown,
              same components, just lives here instead of in-line. */}
          {adminCards.length > 0 && (
            <section style={{ marginTop: 28 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.textMuted,
                  marginBottom: 12,
                }}
              >
                Audit trail &amp; methodology
              </div>
              {adminCards.map((card, i) => (
                <details
                  key={`${card.id}-${i}`}
                  open={i === 0}
                  style={{
                    marginBottom: 12,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  <summary
                    style={{
                      padding: "10px 14px",
                      background: "#f8fafc",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.navy,
                    }}
                  >
                    {card.title || "Section"}
                  </summary>
                  <div style={{ padding: "10px 14px 14px", fontSize: 13 }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {card.lines.join("\n")}
                    </ReactMarkdown>
                  </div>
                </details>
              ))}
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

// ─── Section Card ────────────────────────────────────────────────

/** Hero banner used for "Audit 1 - Organic" / "Audit 2 - Prompted" boundaries.
 *  Replaces the otherwise-empty card those titles would produce, since the
 *  section cards under them already carry their own content. The banner is
 *  full-width navy with a gold underline so it visually anchors the start
 *  of each audit's group of section cards. */
function AuditBanner({ title, id, eyebrow = "Section", subtitle = "" }) {
  return (
    <section
      id={id}
      style={{
        background: C.navy,
        color: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 6px 18px rgba(15,23,42,0.20)",
        padding: "28px 36px",
        margin: "32px 0 18px",
        borderLeft: `6px solid ${C.gold}`,
        breakInside: "avoid",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: C.gold,
          marginBottom: 6,
          opacity: 0.9,
        }}
      >
        {eyebrow}
      </div>
      <h2
        style={{
          fontSize: 30,
          fontWeight: 800,
          color: "#ffffff",
          margin: 0,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h2>
      {subtitle ? (
        <p
          style={{
            fontSize: 13.5,
            fontStyle: "italic",
            color: "rgba(255,255,255,0.82)",
            margin: "12px 0 0",
            maxWidth: 760,
            lineHeight: 1.55,
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </section>
  );
}

function SectionCard({ title, id, content, override, onSaveOverride }) {
  // Editable report - an override (if present) replaces the engine content; the
  // ✏ Edit button opens a markdown editor for this section.
  const hasOverride = typeof override === "string" && override.length > 0;
  const effectiveContent = hasOverride ? override : content;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const startEdit = useCallback(() => {
    setDraft(effectiveContent || "");
    setErr("");
    setEditing(true);
  }, [effectiveContent]);
  const doSave = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      await onSaveOverride?.(id, draft);
      setEditing(false);
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally { setBusy(false); }
  }, [onSaveOverride, id, draft]);
  const doRevert = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      await onSaveOverride?.(id, "");   // empty clears the override
      setEditing(false);
    } catch (e) {
      setErr(e?.message || "Revert failed");
    } finally { setBusy(false); }
  }, [onSaveOverride, id]);

  // `# Audit X - …` cards have their content shipped under the
  // `## S1 / S2 / …` cards beneath them, so the audit-titled card
  // itself is essentially empty. Render those as a hero banner
  // instead of a blank card with just a heading.
  const isAuditBanner =
    /^Audit\s+\d/i.test(title || "") &&
    (!content || !content.trim());
  if (isAuditBanner) {
    return <AuditBanner title={title} id={id} />;
  }
  // Group markers ("Unbranded Audit" / "Branded Audit" / "Shared sections") open a
  // top-level part of the report. Render them as a hero banner with their lead-in as
  // subtitle, so the two independent audits + the shared verdict read as clearly
  // separated, equally-weighted sections.
  const _mk = (title || "").trim().toLowerCase();
  const isAuditTypeBanner = /^(unbranded audit|branded audit|shared sections)$/.test(_mk);
  if (isAuditTypeBanner) {
    const sub = (effectiveContent || "").replace(/^_+|_+$/g, "").trim();
    const eyebrow = _mk === "shared sections" ? "Cross-Audit" : "Audit";
    return <AuditBanner title={title} id={id} eyebrow={eyebrow} subtitle={sub} />;
  }

  // Editable-report controls: an ✏ Edit button (per section) + the inline editor.
  const editable = typeof onSaveOverride === "function" && !!title;
  const editControls = editable && (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "auto" }}
      onClick={(e) => e.preventDefault()}>
      {hasOverride && !editing && (
        <span style={{ fontSize: 10.5, fontWeight: 700, color: "#b45309",
          background: "#fef3c7", padding: "2px 7px", borderRadius: 10 }}>edited</span>
      )}
      {!editing && (
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(); }}
          title="Edit this section"
          style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 8,
            padding: "3px 9px", cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#334155" }}>
          ✏ Edit
        </button>
      )}
    </span>
  );
  const editor = editing && (
    <div style={{ margin: "8px 0 18px" }} onClick={(e) => e.preventDefault()}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        style={{ width: "100%", minHeight: 220, fontFamily: "ui-monospace, Menlo, monospace",
          fontSize: 12.5, lineHeight: 1.55, padding: 12, borderRadius: 10,
          border: `1px solid ${C.border}`, boxSizing: "border-box", resize: "vertical" }} />
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
        <button type="button" onClick={doSave} disabled={busy} className="btn-primary"
          style={{ padding: "6px 14px", borderRadius: 9, fontWeight: 800, fontSize: 12.5,
            opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy}
          style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 9,
            padding: "6px 12px", cursor: "pointer", fontSize: 12.5 }}>Cancel</button>
        {hasOverride && (
          <button type="button" onClick={doRevert} disabled={busy}
            style={{ border: "none", background: "transparent", color: "#b91c1c",
              cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Revert to original</button>
        )}
        <span style={{ fontSize: 11.5, color: C.textMuted }}>Markdown · tables/engine data stay editable but are meant to be left as-is</span>
        {err && <span style={{ color: "#b91c1c", fontSize: 12 }}>{err}</span>}
      </div>
    </div>
  );

  // v2: every section except the summaries is collapsible (dropdown/spoiler).
  // Summaries (executive / audit summaries / overview) stay always-visible.
  const isSummary = /summ|executive|overview|intro|setup/i.test(title || "");
  const h2Style = {
    fontSize: 22,
    fontWeight: 800,
    color: C.navy,
    margin: 0,
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  };
  const sectionStyle = {
    background: C.white,
    borderRadius: 12,
    boxShadow: C.shadow,
    padding: "32px 36px",
    marginBottom: 24,
    border: `1px solid ${C.border}`,
    breakInside: "avoid",
  };
  const body = editing ? editor : (
    <div className="report-section-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {effectiveContent}
      </ReactMarkdown>
    </div>
  );

  if (!title || isSummary) {
    return (
      <section id={id} style={sectionStyle}>
        {title && (
          <h2 style={{ ...h2Style, marginBottom: 20, paddingBottom: 12, borderBottom: `3px solid ${C.gold}`,
            display: "flex", alignItems: "center", gap: 10 }}>
            {title}
            {editControls}
          </h2>
        )}
        {body}
      </section>
    );
  }

  // Collapsible section - <details open> so the report reads normally but any
  // section can be hidden. The ▾ marker rotates via the open state.
  return (
    <section id={id} style={sectionStyle}>
      <details open className="report-collapsible">
        <summary
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            listStyle: "none",
            margin: "0 0 20px",
            paddingBottom: 12,
            borderBottom: `3px solid ${C.gold}`,
          }}
        >
          <span aria-hidden style={{ color: C.gold, fontSize: 16, transition: "transform .15s" }} className="report-collapsible-caret">▾</span>
          <h2 style={h2Style}>{title}</h2>
          {editControls}
        </summary>
        {body}
      </details>
    </section>
  );
}

// ─── TOC Sidebar ─────────────────────────────────────────────────

function tocNodeContains(node, id) {
  if (!node) return false;
  if (node.id === id) return true;
  return (node.children || []).some((c) => tocNodeContains(c, id));
}

// Build the TOC tree by walking the rendered report DOM and stamping a unique,
// sequential id (`toc-N`) onto every heading. Because the tree nodes and the DOM
// elements are the SAME objects, every TOC entry resolves via getElementById -
// this is what kills the old dead-click bug (duplicate heading text like
// "Insights & Actions" ×5 used to collide on slugified ids). Hierarchy rule:
//   • Audit Type  → the single wrapper node ("Unbranded Audit" today).
//   • Section     → an <h2> whose text starts with a number (01–09; the 09a/09c/
//                   09e band collapses into one section 9).
//   • Sub-section → a non-numbered <h2> (Methodology…) or an <h3> under the section.
//   • Table       → an <h4>/<h5>/<h6> nested under the preceding sub-section
//                   (e.g. the "Table S1.4.1.a/b/c" that sit under a Co-Mentions block).
function buildTocFromDom(root) {
  if (!root) return [];
  const els = Array.from(root.querySelectorAll("h2, h3, h4, h5, h6"))
    .filter((el) => (el.textContent || "").trim());
  // Group markers: an <h2> reading "Unbranded Audit" / "Branded Audit" / "Shared sections"
  // starts a new top-level group; sections after it belong to that group. Both-kind reports
  // show Overview (§01/§02) + two audit groups + Shared sections; single-type reports are flat.
  const AUDIT_RE = /^(unbranded audit|branded audit|shared sections)$/i;
  const groupTitle = (t) => {
    const s = t.trim().toLowerCase();
    if (s === "unbranded audit") return "Unbranded Audit";
    if (s === "branded audit") return "Branded Audit";
    return "Shared sections";
  };
  const audits = [];
  let curAudit = null;
  let current = null;      // current Section (level 2) within the audit
  let currentSub = null;   // current Sub-section (level 3) - where level-4 tables attach
  const startAudit = (id, title) => {
    curAudit = { id, title, sections: [], bandSeen: new Map() };
    audits.push(curAudit);
    current = null;
    currentSub = null;
  };
  const stripNum = (t) => t.replace(/^\s*\d+[a-z]?\s*[—–:.-]\s*/, "").trim() || t;
  // Normalized form for sibling de-dup: lowercase, drop a trailing "(…)" qualifier,
  // collapse whitespace. Kills TOC repeats like a section header "LLM Consistency"
  // immediately followed by its self-titled block "LLM Consistency (cross-platform
  // coherence)" - while keeping genuinely distinct siblings (they differ by their
  // "- S1.x.y" code or suffix, which survives normalization).
  const norm = (t) => t.toLowerCase().replace(/\s*\([^)]*\)\s*$/, "").replace(/\s+/g, " ").trim();
  // Heading text minus any nested controls (section cards render an "Edit" button and
  // a collapse caret INSIDE the <h2>; textContent would otherwise pull "…Edit" into
  // the TOC label).
  const cleanText = (el) => {
    const clone = el.cloneNode(true);
    clone.querySelectorAll("button, svg").forEach((n) => n.remove());
    return (clone.textContent || "").replace(/\s+/g, " ").trim();
  };
  const pushDeduped = (parent, node) => {
    const key = norm(node.title);
    if (parent._seen.has(key)) return false;   // adjacent duplicate - id still stamped above
    parent._seen.add(key);
    parent.children.push(node);
    return true;
  };
  els.forEach((el, i) => {
    const id = `toc-${i}`;
    el.id = id;
    const text = cleanText(el);
    const tag = el.tagName;

    // Group boundary → start a new top-level node (the heading itself isn't a section).
    if (tag === "H2" && AUDIT_RE.test(text)) {
      startAudit(id, groupTitle(text));
      return;
    }
    if (!curAudit) startAudit(id, null);   // first audit, before any explicit marker

    const m = tag === "H2" ? text.match(/^(\d+)[a-z]?\b/) : null;
    if (m && !curAudit.bandSeen.has(m[1])) {
      // First card of a new numbered band → a top-level Section. Keep the number
      // as a separate quiet prefix ("09") and the clean label ("Brand Pillars…").
      const node = { id, num: parseInt(m[1], 10), numStr: m[1],
        title: stripNum(text), children: [], _seen: new Set() };
      curAudit.bandSeen.set(m[1], node);
      curAudit.sections.push(node);
      current = node;
      currentSub = null;
      return;
    }

    // Level 4 - <h4>+ that has a sub-section to hang under → a Table.
    if ((tag === "H4" || tag === "H5" || tag === "H6") && current && currentSub) {
      pushDeduped(currentSub, { id, title: text, children: [] });
      return;
    }

    // Level 3 - non-numbered <h2> (Methodology…), an <h3>, or an orphan <h4>.
    const title = tag === "H2" ? stripNum(text) : text;
    const sub = { id, title, children: [], _seen: new Set() };
    if (current) {
      if (!pushDeduped(current, sub)) return;
    } else {
      curAudit.sections.push(sub);
    }
    currentSub = sub;
  });
  const strip = (n) => { delete n._seen; (n.children || []).forEach(strip); };
  audits.forEach((a) => a.sections.forEach(strip));
  const withSections = audits.filter((a) => a.sections.length);
  if (!withSections.length) return [];
  // The first node has no explicit marker. In a two-audit report it holds the common
  // §01/§02 preamble → "Overview". In a single-kind (flat) report it IS the one audit,
  // labelled by which kind was run.
  if (withSections[0].title === null) {
    if (withSections.length > 1) {
      withSections[0].title = "Overview";
    } else {
      const bodyText = root.textContent || "";
      withSections[0].title = /No unbranded \(organic\) questions/i.test(bodyText)
        ? "Branded Audit"
        : "Unbranded Audit";
    }
  }
  return withSections.map((a) => {
    const t = (a.title || "").toLowerCase();
    const kind = /shared/.test(t) ? "verdict" : /audit$/.test(t) ? "audit" : "overview";
    return {
      id: a.id ?? a.sections[0].id,
      title: a.title,
      audit: true,
      kind,
      children: a.sections,
    };
  });
}

// Table of Contents: Audit Type › Section (1–9) › Sub-section › Table.
// `tree` is built from the live DOM after render (see buildTocFromDom), so every
// node id is stamped onto a real heading element - clicks always resolve.
const _TOC_ACCENT = "#2563eb";
const _TOC_ACCENT_BG = "#eef4ff";
const _TOC_HOVER = "#f5f7fa";

function TocChevron({ open }) {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden
      style={{ transition: "transform .15s ease", transform: open ? "rotate(90deg)" : "none",
        display: "block" }}>
      <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Sub-levels of the TOC (depth 3 = sub-sections, depth 4 = the tables nested under
// them). Recursive so a sub-section that owns tables gets its own collapse chevron
// and rail; leaf rows are plain links. Deeper rows read a touch smaller and fainter.
function TocSubtree({ nodes, depth, activeId, openMap, setOpenMap, go, keyFn }) {
  const deep = depth >= 4;
  return (
    <div style={{ margin: deep ? "1px 0 3px 7px" : "2px 0 7px 20px", paddingLeft: 12,
      borderLeft: `1px solid ${deep ? "#eef1f5" : "#e5eaf1"}` }}>
      {nodes.map((n) => {
        const hasKids = (n.children || []).length > 0;
        const open = openMap[n.id] ?? tocNodeContains(n, activeId);
        const isActive = n.id === activeId;
        const goN = () => { go(n.id); if (hasKids) setOpenMap((m) => ({ ...m, [n.id]: true })); };
        const toggle = (e) => { e.stopPropagation(); setOpenMap((m) => ({ ...m, [n.id]: !open })); };
        return (
          <div key={n.id}>
            <div role="button" tabIndex={0} onClick={goN} onKeyDown={keyFn(goN)} title={n.title}
              style={{ display: "flex", alignItems: "center", cursor: "pointer", borderRadius: 7,
                padding: deep ? "5px 8px 5px 9px" : "6px 8px 6px 10px", marginBottom: 1,
                background: isActive ? _TOC_ACCENT_BG : "transparent",
                transition: "background .13s ease, color .13s ease" }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = _TOC_HOVER; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: deep ? 11.5 : 12, lineHeight: 1.4,
                fontWeight: isActive ? 600 : (deep ? 400 : 450),
                color: isActive ? _TOC_ACCENT : (deep ? "#8b95a6" : "#6b7688"),
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {n.title}
              </span>
              {hasKids && (
                <span role="button" tabIndex={0} onClick={toggle}
                  onKeyDown={keyFn(() => toggle({ stopPropagation() {} }))}
                  aria-label={open ? "Collapse" : "Expand"}
                  style={{ width: 16, height: 16, flexShrink: 0, marginLeft: 4,
                    color: isActive ? _TOC_ACCENT : "#aab3c2", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TocChevron open={open} />
                </span>
              )}
            </div>
            {hasKids && open && (
              <TocSubtree nodes={n.children} depth={depth + 1} activeId={activeId}
                openMap={openMap} setOpenMap={setOpenMap} go={go} keyFn={keyFn} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// NB: rows are <div role="button">, NOT <button> - the app's global <button> rule
// (index.css) paints a dark bg + box-shadow + hover-lift that produced the boxy
// "Vista" look. Divs sidestep it entirely; we style them flat inline.
function TableOfContents({ tree, activeId, onNavigate }) {
  const [openMap, setOpenMap] = useState({});
  const go = useCallback((id) => { if (onNavigate) onNavigate(id); }, [onNavigate]);
  const key = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };
  if (!tree || !tree.length) return null;

  return (
    <nav className="report-toc" aria-label="Report contents"
      style={{ position: "sticky", top: 88, width: 280, flexShrink: 0,
        maxHeight: "calc(100vh - 104px)", overflowY: "auto",
        background: "#ffffff", border: "1px solid #e9edf3", borderRadius: 14,
        padding: "12px 8px 10px",
        boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.05)" }}>
      {tree.map((audit) => (
        <div key={audit.id} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9,
            padding: "9px 11px 8px", marginBottom: 4,
            borderBottom: "2px solid #e6ebf3",
            fontSize: 15, fontWeight: 800, letterSpacing: "0.005em",
            color: "#0f172a" }}>
            <span style={{ width: 4, height: 18, borderRadius: 2, background: _TOC_ACCENT }} />
            {audit.title}
          </div>
          {(audit.children || []).map((sec) => {
            const hasKids = (sec.children || []).length > 0;
            const open = openMap[sec.id] ?? tocNodeContains(sec, activeId);
            const isActive = sec.id === activeId;
            const openSec = () => { go(sec.id); if (hasKids) setOpenMap((m) => ({ ...m, [sec.id]: true })); };
            const toggle = (e) => { e.stopPropagation(); setOpenMap((m) => ({ ...m, [sec.id]: !open })); };
            return (
              <div key={sec.id} style={{ marginBottom: 1 }}>
                <div role="button" tabIndex={0} onClick={openSec} onKeyDown={key(openSec)}
                  title={sec.numStr ? `${sec.numStr} - ${sec.title}` : sec.title}
                  style={{ display: "flex", alignItems: "center", cursor: "pointer", borderRadius: 9,
                    padding: "8px 10px 8px 12px", position: "relative",
                    background: isActive ? _TOC_ACCENT_BG : "transparent",
                    transition: "background .13s ease" }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = _TOC_HOVER; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  {isActive && (
                    <span style={{ position: "absolute", left: 3, top: 8, bottom: 8, width: 3,
                      borderRadius: 3, background: _TOC_ACCENT }} />
                  )}
                  {sec.numStr && (
                    <span style={{ fontSize: 12, fontWeight: 600, marginRight: 8, fontVariantNumeric: "tabular-nums",
                      color: isActive ? _TOC_ACCENT : "#97a1b2" }}>{sec.numStr}</span>
                  )}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13,
                    fontWeight: isActive ? 700 : 600, lineHeight: 1.35,
                    color: isActive ? _TOC_ACCENT : "#334155", whiteSpace: "nowrap",
                    overflow: "hidden", textOverflow: "ellipsis" }}>
                    {sec.title}
                  </span>
                  {hasKids && (
                    <span role="button" tabIndex={0} onClick={toggle} onKeyDown={key(() => toggle({ stopPropagation() {} }))}
                      aria-label={open ? "Collapse section" : "Expand section"}
                      style={{ width: 18, height: 18, flexShrink: 0, marginLeft: 6,
                        color: isActive ? _TOC_ACCENT : "#97a1b2", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <TocChevron open={open} />
                    </span>
                  )}
                </div>
                {hasKids && open && (
                  <TocSubtree nodes={sec.children} depth={3} activeId={activeId}
                    openMap={openMap} setOpenMap={setOpenMap} go={go} keyFn={key} />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

// ─── Loading / Error states ──────────────────────────────────────

function LoadingState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: C.bg,
        color: C.textMuted,
        gap: 16,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: `3px solid ${C.border}`,
          borderTopColor: C.navy,
          animation: "reportSpin 0.8s linear infinite",
        }}
      />
      <span style={{ fontSize: 16, fontWeight: 500 }}>Loading analysis report...</span>
    </div>
  );
}

function ErrorState({ message, onBack }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        gap: 20,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
        }}
      >
        !
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Report not available</h2>
      <p style={{ color: C.textMuted, fontSize: 15, margin: 0, maxWidth: 400, textAlign: "center" }}>
        {message || "The requested analysis session could not be loaded."}
      </p>
      {onBack && (
        <button
          onClick={onBack}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: "none",
            background: C.navy,
            color: C.white,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Go back
        </button>
      )}
    </div>
  );
}

// ─── Inject global styles (print, animations, zebra stripes) ─────

const GLOBAL_STYLE_ID = "analysis-report-styles";

function injectGlobalStyles() {
  if (document.getElementById(GLOBAL_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = GLOBAL_STYLE_ID;
  style.textContent = `
    @keyframes reportSpin {
      to { transform: rotate(360deg); }
    }

    /* Neutralize the global <button> chrome (dark bg, box-shadow, hover-lift from
       index.css) inside the TOC - the sidebar styles itself entirely via inline
       styles and must read as flat text rows, not raised buttons. */
    .report-toc button {
      box-shadow: none !important;
      transform: none !important;
      transition: background-color .12s ease, color .12s ease !important;
    }

    /* Zebra striping for report tables */
    .report-section-content table tbody tr:nth-child(even) td {
      background: #f8fafc;
    }

    /* Collapsible report sections (v2): hide native marker, rotate caret. */
    .report-collapsible > summary { list-style: none; }
    .report-collapsible > summary::-webkit-details-marker { display: none; }
    .report-collapsible .report-collapsible-caret { transform: rotate(0deg); }
    .report-collapsible:not([open]) .report-collapsible-caret { transform: rotate(-90deg); }
    .report-collapsible:not([open]) > summary { margin-bottom: 0 !important; padding-bottom: 0 !important; }

    /* Print styles */
    @media print {
      .report-header-actions,
      .report-toc,
      .report-back-link {
        display: none !important;
      }
      .report-header {
        position: static !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .report-body {
        padding-top: 0 !important;
      }
      .report-main {
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      section {
        break-inside: avoid;
        box-shadow: none !important;
        border: 1px solid #ddd !important;
      }
    }

    /* Responsive: stack TOC above content on smaller screens */
    @media (max-width: 1100px) {
      .report-toc {
        position: static !important;
        width: 100% !important;
        max-height: none !important;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-bottom: 20px;
        padding-right: 0 !important;
      }
      .report-toc button {
        border-left: none !important;
        border-bottom: 2px solid transparent !important;
        border-radius: 6px !important;
        padding: 6px 12px !important;
        white-space: nowrap !important;
      }
      .report-layout {
        flex-direction: column !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Prompt-refinement drawer ────────────────────────────────────
// Lists EVERY configurable Stage-4 prompt, grouped by report section. Each prompt is
// editable + versioned, and regenerable: the five Insights & Actions blocks splice in
// place (fast, one LLM call); every other prompt re-runs the full report (revise the
// linked analysis run) since it feeds the metric engine or a deterministic block.
function SectionRefineDrawer({ sessionId, onClose, onRegenerated, onJumpToNum, onFullRegen }) {
  const [prompts, setPrompts] = useState([]);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);
  const [promptDetail, setPromptDetail] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [filter, setFilter] = useState("all");        // all | section | report
  const [panelWidth, setPanelWidth] = useState(560);   // drag the left edge to resize
  const [inputsKey, setInputsKey] = useState(null);    // which prompt's read-only inputs are open
  const [inputsData, setInputsData] = useState(null);  // { rules, output_schema, audits:[…] }
  const [inputsAudit, setInputsAudit] = useState("unbranded"); // which audit's data_summary to show

  // Resize the drawer by dragging its left edge (the panel is anchored right, so
  // width = viewport width − pointer X).
  const startResize = useCallback((e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const w = window.innerWidth - ev.clientX;
      setPanelWidth(Math.max(420, Math.min(window.innerWidth - 80, w)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    copilotStage4Prompts(sessionId)
      .then((d) => { setPrompts(d.prompts || []); setRun(d.run || null); })
      .catch(() => setPrompts([]))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const openPrompt = async (key) => {
    if (expandedKey === key) { setExpandedKey(null); return; }
    setExpandedKey(key); setPromptDetail(null); setMsg("");
    try { const d = await promptGet(key); setPromptDetail(d); setDraft(d.active_content || d.original || ""); }
    catch (e) { setMsg(e?.message || "Failed to load prompt"); }
  };
  // Read-only "View inputs": the resolved data_summary / rules / rendered prompt this
  // section was handed. Cached — no LLM call, no recompute.
  const toggleInputs = async (key) => {
    if (inputsKey === key) { setInputsKey(null); setInputsData(null); return; }
    setInputsKey(key); setInputsData(null); setInputsAudit("unbranded"); setMsg("");
    try {
      const d = await copilotPromptInputs(sessionId, key);
      setInputsData(d);
      if (d.audits?.length) setInputsAudit(d.audits[0].audit);
    } catch (e) { setMsg(e?.message || "Failed to load inputs"); }
  };
  const savePrompt = async (key) => {
    setBusy("save:" + key); setMsg("");
    try { await promptSaveVersion(key, draft, null); const d = await promptGet(key); setPromptDetail(d);
      setDraft(d.active_content || d.original || "");
      setMsg("Prompt saved as a new active version - now Regenerate to apply."); }
    catch (e) { setMsg(e?.message || "Save failed"); } finally { setBusy(null); }
  };
  // version_no 0 = the code original (revert); >0 = an existing saved version.
  const switchVersion = async (key, versionNo) => {
    setBusy("ver:" + key); setMsg("");
    try {
      if (versionNo === 0) { await promptRevert(key); }
      else { await promptActivate(key, versionNo); }
      const d = await promptGet(key); setPromptDetail(d);
      setDraft(d.active_content || d.original || "");
      setMsg(versionNo === 0
        ? "Reverted to the original prompt - Regenerate to apply."
        : `Switched to version v${versionNo} - Regenerate to apply.`);
    } catch (e) { setMsg(e?.message || "Switch failed"); } finally { setBusy(null); }
  };
  const regenerate = async (p) => {
    setMsg("");
    if (p.mode === "section" && (p.section_id || p.block_key)) {
      setBusy("regen:" + p.key);
      try {
        const r = p.section_id
          ? await copilotRegenerateSection(sessionId, p.section_id)
          : await copilotRegenerateBlock(sessionId, p.block_key);
        if (!r.applied) {
          setMsg("Regenerated, but couldn't splice it in - try the full report regen.");
        } else if (r.changed === false) {
          setMsg("Regenerated, but the output was identical - your prompt edit didn't change "
            + "the result. Change WHAT the model analyses to move the output.");
        } else {
          setMsg("Regenerated and updated in the report ✓");
        }
        await onRegenerated?.();
      } catch (e) { setMsg(e?.message || "Regeneration failed"); } finally { setBusy(null); }
      return;
    }
    // report mode - full re-run of the whole analysis
    if (!run) {
      setMsg("This report isn't linked to an analysis run, so a full re-run isn't available here. "
        + "Edit on /prompts, then regenerate from the analysis wizard.");
      return;
    }
    const ok = window.confirm(
      "Regenerate the FULL report?\n\nThis re-runs all Stage-4 analysis (~1–2 min) with your current "
      + "prompt versions and opens the run. In-place edits to this report are replaced by the new one.");
    if (!ok) return;
    setBusy("regen:" + p.key);
    try { await onFullRegen(run.id, run.brand_id); }
    catch (e) { setMsg(e?.message || "Could not start the full re-run."); setBusy(null); }
  };

  const counts = {
    all: prompts.length,
    section: prompts.filter((p) => p.mode === "section").length,
    report: prompts.filter((p) => p.mode === "report").length,
  };
  const visible = filter === "all" ? prompts : prompts.filter((p) => p.mode === filter);

  // Group the visible prompts by report-section label so the drawer mirrors the report.
  const groups = [];
  const seen = new Map();
  for (const p of visible) {
    if (!seen.has(p.section)) { seen.set(p.section, groups.length); groups.push([p.section, []]); }
    groups[seen.get(p.section)][1].push(p);
  }

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.12)", zIndex: 200,
    display: "flex", justifyContent: "flex-end" };
  const panel = { position: "relative", width: panelWidth, maxWidth: "96vw", height: "100%",
    background: "#fff", color: "#0f172a", overflowY: "auto", padding: 20,
    boxShadow: "-12px 0 40px rgba(0,0,0,0.2)" };
  const smallBtn = { border: "1px solid var(--border)", background: "#fff", color: "#334155",
    borderRadius: 8, padding: "5px 9px", cursor: "pointer", fontSize: 12 };

  const renderVersions = (key) => (
    <div style={{ marginTop: 12, borderTop: "1px dashed var(--border)", paddingTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6,
        textTransform: "uppercase", letterSpacing: "0.04em" }}>Version</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {(() => {
          const activeNo = promptDetail.active_version_no || 0;
          const chip = (no, text, title) => {
            const on = activeNo === no;
            return (
              <button key={no} type="button" title={title} disabled={!!busy}
                onClick={() => !on && switchVersion(key, no)}
                style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                  cursor: on ? "default" : "pointer",
                  border: `1px solid ${on ? "#2563eb" : "var(--border)"}`,
                  background: on ? "#2563eb" : "#fff", color: on ? "#fff" : "#334155" }}>
                {text}{on ? " ✓" : ""}
              </button>
            );
          };
          return [
            chip(0, "Original", "The prompt shipped in code - revert to this"),
            ...(promptDetail.versions || []).map((v) =>
              chip(v.version_no, "v" + v.version_no,
                (v.label ? v.label + " · " : "") +
                (v.created_at ? new Date(v.created_at).toLocaleString() : ""))),
          ];
        })()}
      </div>
      {(promptDetail.active_version_no || 0) > 0 && (
        <button type="button" onClick={() => switchVersion(key, 0)} disabled={!!busy}
          style={{ marginTop: 8, border: "1px solid var(--border)", background: "#fff",
            color: "#b45309", borderRadius: 8, padding: "5px 10px", cursor: "pointer",
            fontSize: 12, fontWeight: 700 }}>
          ↺ Revert to original
        </button>
      )}
    </div>
  );

  const filterTabs = [
    ["all", "All", counts.all, "#334155", "#e2e8f0", "Show every prompt"],
    ["section", "Local regen", counts.section, "#166534", "#86efac", "Local regen permitted - splices in place"],
    ["report", "Global regen", counts.report, "#92400e", "#fcd34d", "Global regen needed - re-runs the full report"],
  ];

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        {/* Left-edge resize handle - drag to widen/narrow the drawer. */}
        <div onMouseDown={startResize} title="Drag to resize"
          style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 8, cursor: "ew-resize", zIndex: 3 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(37,99,235,0.25)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontWeight: 900, fontSize: 17 }}>Refine Stage-4 prompts</div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent",
            fontSize: 20, cursor: "pointer", color: "#64748b" }}>×</button>
        </div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 12 }}>
          Every prompt that builds this report - editable and versioned. <b>Insight</b> blocks
          regenerate in place; the rest re-run the full report with your current versions.
        </div>
        {/* Filter by regeneration scope. */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {filterTabs.map(([val, lbl, ct, fg, activeBg, title]) => {
            const on = filter === val;
            return (
              <button key={val} type="button" title={title} onClick={() => setFilter(val)}
                style={{ flex: 1, borderRadius: 8, padding: "6px 8px", fontSize: 11.5, fontWeight: 700,
                  cursor: "pointer", color: on ? "#0f172a" : "#64748b",
                  border: `1px solid ${on ? fg : "var(--border)"}`,
                  background: on ? activeBg : "#fff",
                  boxShadow: on ? "inset 0 -2px 0 " + fg : "none" }}>
                {lbl} <span style={{ opacity: 0.7 }}>({ct})</span>
              </button>
            );
          })}
        </div>
        {msg && (
          <div style={{ fontSize: 12.5, color: "#1e40af", background: "#eff6ff",
            border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>{msg}</div>
        )}
        {loading ? (
          <div style={{ color: "#64748b" }}>Loading…</div>
        ) : groups.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>
            {prompts.length ? "No prompts match this filter." : "No configurable prompts found for this report."}
          </div>
        ) : (
          groups.map(([section, items]) => (
            <div key={section} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase",
                color: "#475569", margin: "0 0 8px 2px" }}>{section}</div>
              {items.map((p) => (
                <div key={p.key} style={{ border: "1px solid var(--border)", borderRadius: 10,
                  padding: 12, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, flex: 1, minWidth: 150 }}>
                      {p.label}
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px",
                        borderRadius: 999, verticalAlign: "middle", whiteSpace: "nowrap",
                        color: p.mode === "section" ? "#166534" : "#92400e",
                        background: p.mode === "section" ? "#dcfce7" : "#fef3c7" }}>
                        {p.mode === "section" ? "Local regen permitted" : "Global regen needed"}
                      </span>
                    </div>
                    {p.jump_num != null && onJumpToNum && (
                      <button type="button" style={smallBtn} title="Scroll the report to this prompt's output"
                        onClick={() => { if (!onJumpToNum(p.jump_num, p.jump_match)) setMsg("Couldn't locate that block in the report."); }}>
                        ↧ Jump
                      </button>
                    )}
                    {p.section_id && (
                      <button type="button" style={smallBtn} title="See the exact data + rules this prompt is handed (read-only)"
                        onClick={() => toggleInputs(p.key)}>
                        {inputsKey === p.key ? "Hide inputs" : "🔍 View inputs"}
                      </button>
                    )}
                    <button type="button" style={smallBtn} onClick={() => openPrompt(p.key)}>
                      {expandedKey === p.key ? "Hide" : "Edit"}
                    </button>
                    <button type="button" onClick={() => regenerate(p)} disabled={!!busy}
                      className="btn-primary" style={{ padding: "5px 12px", borderRadius: 8, fontWeight: 800,
                        fontSize: 12, opacity: busy ? 0.6 : 1 }}>
                      {busy === "regen:" + p.key ? "Working…" : (p.mode === "section" ? "Regenerate" : "Regenerate report")}
                    </button>
                  </div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 4 }}><code>{p.key}</code></div>
                  {expandedKey === p.key && (
                    promptDetail ? (
                      <div style={{ marginTop: 10 }}>
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false}
                          style={{ width: "100%", minHeight: 200, fontFamily: "ui-monospace, Menlo, monospace",
                            fontSize: 12, lineHeight: 1.5, padding: 10, borderRadius: 8, color: "#0f172a",
                            background: "#fff", border: "1px solid var(--border)", boxSizing: "border-box" }} />
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                          <button type="button" onClick={() => savePrompt(p.key)} disabled={!!busy}
                            className="btn-primary" style={{ padding: "6px 14px", borderRadius: 8, fontWeight: 800, fontSize: 12.5 }}>
                            {busy === "save:" + p.key ? "Saving…" : "Save as new version"}
                          </button>
                          {draft !== (promptDetail.active_content || promptDetail.original || "") && (
                            <span style={{ fontSize: 11, color: "#b45309" }}>unsaved edits</span>
                          )}
                        </div>
                        {renderVersions(p.key)}
                      </div>
                    ) : <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>Loading prompt…</div>
                  )}
                  {inputsKey === p.key && (
                    inputsData ? (() => {
                      const pre = { whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, Menlo, monospace",
                        fontSize: 11, lineHeight: 1.5, padding: 10, borderRadius: 8, color: "#0f172a",
                        background: "#f8fafc", border: "1px solid var(--border)", margin: "4px 0 0",
                        maxHeight: 320, overflow: "auto" };
                      const lbl = { fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase",
                        letterSpacing: "0.04em", margin: "12px 0 0" };
                      const cur = (inputsData.audits || []).find((a) => a.audit === inputsAudit) || (inputsData.audits || [])[0];
                      return (
                        <div style={{ marginTop: 10, borderTop: "1px dashed var(--border)", paddingTop: 10 }}>
                          <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 4 }}>
                            Read-only · the exact strings this prompt is handed at generation time (cached — no LLM call).
                          </div>
                          <div style={lbl}>data_summary <span style={{ textTransform: "none", fontWeight: 500, color: "#94a3b8" }}>— different per section</span></div>
                          {(inputsData.audits || []).length > 1 && (
                            <div style={{ display: "flex", gap: 6, margin: "6px 0 2px" }}>
                              {inputsData.audits.map((a) => (
                                <button key={a.audit} type="button" onClick={() => setInputsAudit(a.audit)}
                                  style={{ ...smallBtn, fontSize: 11, fontWeight: 700,
                                    borderColor: inputsAudit === a.audit ? "#2563eb" : "var(--border)",
                                    color: inputsAudit === a.audit ? "#2563eb" : "#334155" }}>
                                  {a.audit === "branded" ? "Branded audit" : "Unbranded audit"}
                                </button>
                              ))}
                            </div>
                          )}
                          <pre style={pre}>{cur ? cur.data_summary : (inputsData.note || "—")}</pre>
                          <div style={lbl}>rules <span style={{ textTransform: "none", fontWeight: 500, color: "#94a3b8" }}>— shared across all insight prompts</span></div>
                          <pre style={pre}>{inputsData.rules}</pre>
                          <details style={{ marginTop: 10 }}>
                            <summary style={{ cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#475569" }}>
                              output_schema + full rendered prompt
                            </summary>
                            <div style={lbl}>output_schema <span style={{ textTransform: "none", fontWeight: 500, color: "#94a3b8" }}>— static</span></div>
                            <pre style={pre}>{inputsData.output_schema}</pre>
                            <div style={lbl}>rendered prompt <span style={{ textTransform: "none", fontWeight: 500, color: "#94a3b8" }}>— what the model receives</span></div>
                            <pre style={pre}>{cur ? cur.rendered : "—"}</pre>
                          </details>
                        </div>
                      );
                    })() : <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>Loading inputs…</div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export default function AnalysisReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [auditQuestions, setAuditQuestions] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [exporting, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [showQuestionsDialog, setShowQuestionsDialog] = useState(false);
  const [showContextDialog, setShowContextDialog] = useState(false);
  const [contextLayers, setContextLayers] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [linkedRun, setLinkedRun] = useState(null); // { id, brand_id } or null
  // ── Inline-quote drawer state ──
  // Owned at the page level so a single drawer instance handles every
  // citation in the report. The QuoteRef components dispatch
  // `openAnswer(qNum, llm)` via context to set this.
  const [activeAnswer, setActiveAnswer] = useState(null); // { qNum, llm } | null
  const [activeDomain, setActiveDomain] = useState(null); // domain string | null
  const [showAdminDrawer, setShowAdminDrawer] = useState(false);
  const [showRefine, setShowRefine] = useState(false);   // prompt-refinement drawer

  // Re-fetch just the report content (used after a single-section regeneration).
  const reloadReport = useCallback(async () => {
    try {
      const data = await copilotGetSession(sessionId);
      if (data?.session) {
        setMessages(data.messages || []);
        setSectionOverrides(data.session.section_overrides || {});
      }
    } catch { /* ignore */ }
  }, [sessionId]);
  // Editable report - per-section markdown overrides { slug: markdown }.
  const [sectionOverrides, setSectionOverrides] = useState({});

  const contentRef = useRef(null);

  // ── Fetch session data ──
  useEffect(() => {
    injectGlobalStyles();

    let cancelled = false;

    async function load() {
      try {
        const [data, brands] = await Promise.all([
          copilotGetSession(sessionId),
          fetchBrands().catch(() => []),
        ]);

        if (cancelled) return;

        if (!data || !data.session) {
          setError("Session not found.");
          setLoading(false);
          return;
        }

        setSession(data.session);
        setSectionOverrides(data.session.section_overrides || {});
        setMessages(data.messages || []);
        setAuditQuestions(data.audit_questions || []);

        // Resolve brand name
        const brandId = data.session.brand_id;
        const allBrands = Array.isArray(brands) ? brands : brands?.items || [];
        const brand = allBrands.find((b) => b.id === brandId);
        setBrandName(brand?.name || `Brand #${brandId}`);
        setLoading(false);

        // Look up the analysis_run linked to this session so we can offer a
        // "Open in Analysis flow" shortcut back to the wizard. Best-effort -
        // not every session has a run (legacy sessions created before the
        // Analysis Run entity don't).
        try {
          const runs = await analysisRunsList({ brandId });
          const match = (runs?.items || []).find(
            (r) => Number(r.copilot_session_id) === Number(sessionId)
          );
          if (!cancelled && match) {
            setLinkedRun({ id: match.id, brand_id: match.brand_id });
          }
        } catch {
          /* silent */
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load session.");
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Build analysis content from assistant messages ──
  const analysisMarkdown = useMemo(() => {
    return messages
      .filter((m) => m.role === "assistant" && m.status !== "error" && (m.text || "").trim())
      .map((m) => m.text.trim())
      .join("\n\n---\n\n");
  }, [messages]);

  const allCards = useMemo(() => splitIntoCards(analysisMarkdown), [analysisMarkdown]);

  // Audit Trail + Methodology Version + Recent changes + Data Quality
  // Report are admin / compliance metadata that bury the substance of
  // the report when rendered inline. Filter them out of the main flow
  // and surface them behind a ⚙ settings icon in the header instead.
  const _ADMIN_TITLE_RE = /^(audit trail|methodology version|recent changes|data quality report|data quality note)\b/i;
  const { cards, adminCards } = useMemo(() => {
    const main = [];
    const admin = [];
    for (const c of allCards) {
      if (c.title && _ADMIN_TITLE_RE.test(c.title.trim())) admin.push(c);
      else main.push(c);
    }
    return { cards: main, adminCards: admin };
  }, [allCards]);

  // ── Table of Contents: built from the live DOM after each render ──
  // buildTocFromDom stamps a unique `toc-N` id on every rendered heading and returns
  // the 3-level tree (Audit › Section › Sub-section). Because ids are assigned to the
  // very elements the tree references, every TOC click resolves - no dead anchors.
  const [toc, setToc] = useState([]);
  // While a click-scroll is animating, the IntersectionObserver would otherwise fire
  // for every heading the page sweeps past, racing the highlight through each item.
  // navigateTo locks the observer, sets the target active immediately, and releases
  // on `scrollend` (with a timeout ceiling as a fallback).
  const scrollLockRef = useRef(false);
  const navTimerRef = useRef(null);
  const navigateTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return false;
    scrollLockRef.current = true;
    setActiveSection(id);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    const onEnd = () => {
      window.removeEventListener("scrollend", onEnd);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      scrollLockRef.current = false;
    };
    window.addEventListener("scrollend", onEnd);
    navTimerRef.current = setTimeout(() => {
      window.removeEventListener("scrollend", onEnd);
      scrollLockRef.current = false;
    }, 1200);
    return true;
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) { setToc([]); return; }
    let observer = null;
    const t = setTimeout(() => {
      const tree = buildTocFromDom(root);
      setToc(tree);
      // Track the active heading for the TOC highlight - observe every stamped id.
      observer = new IntersectionObserver(
        (entries) => {
          if (scrollLockRef.current) return;   // ignore intermediate headings mid click-scroll
          for (const entry of entries) {
            if (entry.isIntersecting) setActiveSection(entry.target.id);
          }
        },
        { rootMargin: "-80px 0px -60% 0px", threshold: 0.1 },
      );
      root.querySelectorAll('[id^="toc-"]').forEach((el) => observer.observe(el));
    }, 60);
    return () => { clearTimeout(t); if (observer) observer.disconnect(); };
  }, [analysisMarkdown, sectionOverrides, cards]);

  // Scroll the report to the block a prompt produces. `num` picks the Section; when
  // `match` is given, search that section's subtree for the sub-section/table whose
  // title contains it (disambiguates the five "Insights & Actions" blocks by section).
  const jumpToReportNum = useCallback((num, match) => {
    const secs = (toc || []).flatMap((a) => a.children || []);   // across all audit groups
    const section = secs.find((s) => s.num === num);
    if (!section) return false;
    let target = section;
    if (match) {
      const needle = match.toLowerCase();
      const find = (node) => {
        if ((node.title || "").toLowerCase().includes(needle)) return node;
        for (const c of (node.children || [])) { const f = find(c); if (f) return f; }
        return null;
      };
      // Skip the section node itself so a generic section title can't shadow a child.
      for (const c of (section.children || [])) { const f = find(c); if (f) { target = f; break; } }
    }
    if (!navigateTo(target.id)) return false;
    const el = document.getElementById(target.id);
    if (el) {
      el.style.transition = "background-color .35s";
      el.style.backgroundColor = "rgba(37,99,235,0.14)";
      setTimeout(() => { el.style.backgroundColor = ""; }, 1500);
    }
    return true;
  }, [toc, navigateTo]);

  // Full report re-run: revise the linked analysis run (fresh Stage-4 with current
  // prompt versions) and open the wizard, which drives the re-analysis to a new report.
  const fullRegen = useCallback(async (runId, brandId) => {
    await analysisRunRevise(runId);
    navigate(`/brands/${brandId}/new-analysis?runId=${runId}`);
  }, [navigate]);

  // ── Export handler ──
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await copilotExportPptx(Number(sessionId));
    } catch (err) {
      alert("Export failed: " + (err?.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }, [exporting, sessionId]);

  // ── Handle print ──
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // ── Navigate back ──
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Render states ──
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onBack={handleBack} />;
  if (!analysisMarkdown.trim()) {
    return <ErrorState message="This session has no analysis content to display." onBack={handleBack} />;
  }

  const modelName = session?.default_model || messages.find((m) => m.model)?.model || "-";
  const sessionDate = formatDate(session?.created_at);
  const sessionTitle = session?.title || "Analysis Report";

  // Quote-reference context value: gives every QuoteRef component
  // access to the answer_index lookup + the open-drawer dispatcher.
  // Memoised by reference so the (Q#, LLM) → answer subtree doesn't
  // remount on unrelated state changes.
  const quoteRefValue = {
    answerIndex: session?.answer_index || null,
    openAnswer: (qNum, llm) => setActiveAnswer({ qNum, llm }),
    openDomain: (domain) => setActiveDomain(domain),
  };
  const activeEntry = activeAnswer
    ? (session?.answer_index?.[`Q${activeAnswer.qNum}`]?.[activeAnswer.llm] || null)
    : null;

  return (
    <QuoteRefContext.Provider value={quoteRefValue}>
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* ── Header ── */}
      <header
        className="report-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: C.navy,
          color: C.white,
          padding: "0 32px",
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20, minWidth: 0 }}>
          <button
            className="report-back-link"
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              cursor: "pointer",
              padding: "6px 0",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = C.white; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div
            style={{
              width: 1,
              height: 28,
              background: "rgba(255,255,255,0.15)",
              flexShrink: 0,
            }}
          />

          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: C.gold,
                flexShrink: 0,
              }}
            >
              {brandName}
            </span>
            <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sessionTitle}
            </span>
          </div>
        </div>

        <div className="report-header-actions" style={{ display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setShowAdminDrawer(true)}
            title="Configuration & metadata (cost, methodology, audit trail)"
            aria-label="Open configuration & metadata"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.10)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            {/* gear icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => setShowRefine(true)}
            title="Edit a section's prompt and regenerate just that section"
            style={{
              padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent", color: C.white, fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.10)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            ⚙ Refine prompts
          </button>

          <button
            onClick={handlePrint}
            title="Print report"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>

          <button
            onClick={async () => {
              try {
                const { copilotExportMd } = await import("../services/api");
                await copilotExportMd(sessionId);
              } catch {}
            }}
            title="Download as Markdown"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Export MD
          </button>

          {linkedRun && (
            <button
              onClick={() =>
                navigate(
                  `/brands/${linkedRun.brand_id}/new-analysis?runId=${linkedRun.id}`
                )
              }
              title="Open this analysis in the 4-stage wizard (chat with MAD Pilot, revise, regenerate PPTX)"
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${C.navy}`,
                background: "#fff",
                color: C.navy,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.navy;
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = C.navy;
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Open in Analysis flow
            </button>
          )}

          <button
            onClick={handleExport}
            disabled={exporting}
            title="Export as PowerPoint"
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: "none",
              background: C.gold,
              color: C.navy,
              fontSize: 13,
              fontWeight: 700,
              cursor: exporting ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              opacity: exporting ? 0.7 : 1,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.background = C.goldDark; }}
            onMouseLeave={(e) => { if (!exporting) e.currentTarget.style.background = C.gold; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {exporting ? "Exporting..." : "Export PPTX"}
          </button>
        </div>
      </header>

      {/* ── Body: TOC sidebar + main content ── */}
      <div
        className="report-body"
        style={{
          paddingTop: 32,
          paddingBottom: 64,
        }}
      >
        <div
          className="report-layout"
          style={{
            display: "flex",
            gap: 32,
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            alignItems: "flex-start",
          }}
        >
          {toc.length > 0 && (
            <TableOfContents tree={toc} activeId={activeSection} onNavigate={navigateTo} />
          )}

          <main
            className="report-main"
            ref={contentRef}
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Title card */}
            <div
              style={{
                background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 100%)`,
                borderRadius: 12,
                padding: "36px 40px",
                marginBottom: 28,
                color: C.white,
                boxShadow: C.shadowLg,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: C.gold,
                  marginBottom: 8,
                }}
              >
                MAD Discovery Analysis Report
              </div>
              <h1
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  margin: "0 0 12px",
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                {sessionTitle}
              </h1>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Brand:</strong> {brandName}
                </span>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Model:</strong> {modelName}
                </span>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Date:</strong> {sessionDate}
                </span>
                {/* Cost intentionally omitted from the client-facing title
                    card - it lives in the ⚙ admin drawer only. */}
              </div>
            </div>

            {/* Section cards */}
            {cards.map((card, i) => (
              <SectionCard
                key={card.id + "-" + i}
                title={card.title}
                id={card.id}
                content={card.lines.join("\n")}
                override={sectionOverrides[card.id]}
                onSaveOverride={async (slug, markdown) => {
                  const res = await copilotSetSectionOverride(sessionId, slug, markdown);
                  setSectionOverrides(res?.section_overrides || {});
                }}
              />
            ))}

            {/* Footer */}
            <div
              style={{
                textAlign: "center",
                padding: "32px 0 0",
                color: C.textMuted,
                fontSize: 13,
              }}
            >
              Generated by MAD Discovery - {sessionDate}
            </div>
          </main>
        </div>
      </div>

      {/* Questions Dialog */}
      {showQuestionsDialog && (
        <div onClick={() => setShowQuestionsDialog(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 700, width: "100%", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>Audit Questions</h2>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{auditQuestions.length} questions used in this audit</p>
              </div>
              <button onClick={() => setShowQuestionsDialog(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af", padding: "4px 8px" }}>&times;</button>
            </div>
            <div style={{ overflow: "auto", padding: "16px 24px" }}>
              {auditQuestions.length === 0 ? (
                <p style={{ color: "#9ca3af", fontSize: 13 }}>No questions available for this session.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb", width: 50 }}>#</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb" }}>Question</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb", width: 60 }}>Lang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditQuestions.map((q) => (
                      <tr key={q.number} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: C.navy }}>Q{q.number}</td>
                        <td style={{ padding: "8px 12px", color: "#1e293b" }}>{q.text}</td>
                        <td style={{ padding: "8px 12px", color: "#9ca3af", fontSize: 12 }}>{q.language}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Context Blob Dialog */}
      {showContextDialog && (
        <div onClick={() => setShowContextDialog(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 900, width: "100%", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>Context Blob</h2>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {contextLayers ? `${(contextLayers.total_chars / 1000).toFixed(0)}K characters across ${contextLayers.layers?.length || 0} layers` : "Loading..."}
                </p>
              </div>
              <button onClick={() => setShowContextDialog(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af", padding: "4px 8px" }}>&times;</button>
            </div>
            <div style={{ overflow: "auto", padding: "16px 24px" }}>
              {contextLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading context blob...</div>
              ) : contextLayers?.layers?.length > 0 ? (
                contextLayers.layers.map((layer, i) => (
                  <details key={i} style={{ marginBottom: 12, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}
                    open={i === 0}>
                    <summary style={{
                      padding: "12px 16px", background: "#f8fafc", cursor: "pointer", fontWeight: 600, fontSize: 14, color: C.navy,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span>{layer.title}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>{(layer.content.length / 1000).toFixed(1)}K chars</span>
                    </summary>
                    <pre style={{
                      padding: 16, margin: 0, fontSize: 11, lineHeight: 1.5, color: "#374151",
                      background: "#fafbfc", overflow: "auto", maxHeight: 400, whiteSpace: "pre-wrap", wordBreak: "break-word",
                      fontFamily: "'SF Mono', Monaco, Consolas, monospace",
                    }}>
                      {layer.content}
                    </pre>
                  </details>
                ))
              ) : (
                <p style={{ color: "#9ca3af", fontSize: 13 }}>No context blob available for this session.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {showRefine && (
        <SectionRefineDrawer
          sessionId={sessionId}
          onClose={() => setShowRefine(false)}
          onRegenerated={reloadReport}
          onJumpToNum={jumpToReportNum}
          onFullRegen={fullRegen}
        />
      )}
      <AnswerDrawer
        open={!!activeAnswer && !!activeEntry}
        qNum={activeAnswer?.qNum}
        llm={activeAnswer?.llm}
        entry={activeEntry}
        onClose={() => setActiveAnswer(null)}
      />
      <DomainDrawer
        open={!!activeDomain}
        domain={activeDomain}
        brandId={session?.brand_id}
        auditId={session?.audit_id}
        onClose={() => setActiveDomain(null)}
      />
      <AdminMetaDrawer
        open={showAdminDrawer}
        onClose={() => setShowAdminDrawer(false)}
        session={session}
        brandName={brandName}
        modelName={modelName}
        sessionDate={sessionDate}
        adminCards={adminCards}
        auditQuestionsCount={auditQuestions.length}
        onShowQuestions={() => {
          setShowAdminDrawer(false);
          setShowQuestionsDialog(true);
        }}
        onShowContext={async () => {
          setShowAdminDrawer(false);
          setShowContextDialog(true);
          if (!contextLayers) {
            setContextLoading(true);
            try {
              const { copilotGetContextBlob } = await import("../services/api");
              const data = await copilotGetContextBlob(sessionId);
              setContextLayers(data);
            } catch {
              setContextLayers({ layers: [], total_chars: 0 });
            } finally {
              setContextLoading(false);
            }
          }
        }}
      />
    </div>
    </QuoteRefContext.Provider>
  );
}
